import React, { useMemo, useState } from 'react'
import {
  Box,
  Checkbox,
  FormControl,
  IconButton,
  ListItemText,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import CloseIcon from '@mui/icons-material/Close'
import { msg, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import {
  MAPLIBRE_CTRL_BTN_SIZE,
  maplibreGroupChildIconButtonSx,
} from '../../consts/map-floating-control-sx'
import {
  mapAnalysisActionBtnSx,
  mapAnalysisPanelHeaderSx,
  mapAnalysisPanelShellSx,
  mapAnalysisPanelTokens,
  mapAnalysisTitleSx,
} from '../../consts/map-analysis-panel-sx'

/**
 * Isolates year filtering from the Analysis toolbox.
 * Funnel control (+ optional sibling square control) + collapsible year panel.
 */
export const MAP_ANALYSIS_TOOLBAR_WIDTH = 380

export default function MapYearFilterToolbar({
  availableYears = [],
  selectedYears = [],
  onSelectedYearsChange,
  disabled = false,
  /** Square control rendered immediately after the year-filter button (e.g. analysis toggle). */
  afterFilterButton = null,
}) {
  const theme = useTheme()
  const { _ } = useLingui()
  const [open, setOpen] = useState(false)

  const hasYears = Array.isArray(availableYears) && availableYears.length > 0
  const selected = useMemo(
    () => (Array.isArray(selectedYears) ? selectedYears : []),
    [selectedYears],
  )
  const floatSx = maplibreGroupChildIconButtonSx(theme)
  const tokens = mapAnalysisPanelTokens(theme)
  const isActive = open || selected.length > 0

  const selectedLabel = useMemo(() => {
    if (selected.length === 0) return _(msg`No year`)
    if (selected.length === 1) return String(selected[0])
    return `${selected.length} years`
  }, [_, selected])

  const handleChange = (event) => {
    const raw = event.target.value
    const next = (typeof raw === 'string' ? raw.split(',') : raw)
      .map((y) => Number(y))
      .filter((y) => Number.isFinite(y) && y >= 1)
      .sort((a, b) => b - a)
    onSelectedYearsChange?.(next)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 1,
        pointerEvents: 'auto',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          gap: 1,
        }}
      >
        <Box
          className="maplibregl-ctrl maplibregl-ctrl-group"
          sx={{
            borderRadius: 'var(--radius-lg, 8px)',
            overflow: 'hidden',
            bgcolor: tokens.surfaceBg,
          }}
        >
          <Tooltip title={_(msg`Year filter`)} placement="right">
            <span>
              <IconButton
                className="fred-map-toolbar-btn"
                size="small"
                onClick={() => setOpen((prev) => !prev)}
                disabled={disabled}
                aria-label={_(msg`Year filter`)}
                aria-expanded={open}
                aria-pressed={isActive}
                sx={{
                  ...floatSx,
                  width: MAPLIBRE_CTRL_BTN_SIZE,
                  height: MAPLIBRE_CTRL_BTN_SIZE,
                  color: isActive ? tokens.accent : floatSx.color,
                  bgcolor: isActive ? tokens.activeRowBg : floatSx.backgroundColor,
                  '&:hover': {
                    bgcolor: isActive ? tokens.activeRowBg : floatSx['&:hover']?.backgroundColor,
                    color: tokens.accent,
                  },
                  // FilterAlt glyph sits optically high in the viewBox — nudge down.
                  '& .MuiSvgIcon-root': {
                    fontSize: 18,
                    position: 'relative',
                    top: 3,
                  },
                }}
              >
                <FilterAltIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        {afterFilterButton}
      </Box>

      {open && (
        <Box
          sx={{
            ...mapAnalysisPanelShellSx(theme),
            width: MAP_ANALYSIS_TOOLBAR_WIDTH,
          }}
        >
          <Box sx={mapAnalysisPanelHeaderSx(theme)}>
            <Typography variant="caption" sx={mapAnalysisTitleSx(theme)}>
              <Trans>Year</Trans>
            </Typography>
            <Tooltip title={_(msg`Collapse`)} placement="bottom">
              <IconButton
                size="small"
                onClick={() => setOpen(false)}
                aria-label={_(msg`Collapse year filter`)}
                sx={mapAnalysisActionBtnSx(theme)}
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ px: 1.5, py: 1.25 }}>
            <FormControl size="small" fullWidth disabled={disabled || !hasYears}>
              <Select
                multiple
                displayEmpty
                value={selected}
                onChange={handleChange}
                renderValue={() => selectedLabel}
                aria-label={_(msg`Filter years`)}
                sx={{
                  fontSize: 'var(--font-md, 13px)',
                  height: 36,
                  borderRadius: 'var(--radius-lg, 8px)',
                  color: tokens.textPrimary,
                  bgcolor: tokens.surfaceBg,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: tokens.rowBorder,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: tokens.gray,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: tokens.accent,
                    borderWidth: 1,
                  },
                  '& .MuiSelect-icon': {
                    color: tokens.textPrimary,
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 280,
                      borderRadius: 'var(--radius-2xl, 12px)',
                      boxShadow: theme.shadows[8],
                      border: `1px solid ${tokens.rowBorder}`,
                      mt: 0.5,
                    },
                  },
                }}
              >
                {!hasYears && (
                  <MenuItem value="" disabled>
                    <Trans>No years</Trans>
                  </MenuItem>
                )}
                {availableYears.map((year) => {
                  const checked = selected.includes(year)
                  return (
                    <MenuItem
                      key={year}
                      value={year}
                      dense
                      sx={{
                        borderRadius: 'var(--radius-md, 6px)',
                        mx: 0.5,
                        my: 0.25,
                        color: tokens.textPrimary,
                        bgcolor: checked ? tokens.activeRowBg : 'transparent',
                        '&:hover': {
                          bgcolor: checked ? tokens.activeRowBg : tokens.rowHoverBg,
                        },
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={checked}
                        sx={{
                          py: 0,
                          pl: 0,
                          color: tokens.gray,
                          '&.Mui-checked': { color: tokens.accent },
                        }}
                      />
                      <ListItemText
                        primary={year}
                        primaryTypographyProps={{
                          fontSize: 'var(--font-md, 13px)',
                          fontWeight: checked ? 600 : 400,
                          color: tokens.textPrimary,
                        }}
                      />
                    </MenuItem>
                  )
                })}
              </Select>
            </FormControl>
            {selected.length !== 1 && (
              <Typography
                variant="caption"
                sx={{ display: 'block', mt: 1, color: tokens.textSecondary }}
              >
                <Trans>Heatmap and road risk require a single year.</Trans>
              </Typography>
            )}
          </Box>
        </Box>
      )}
    </Box>
  )
}
